import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDocumentDownloadUrl } from '@/hooks/useDocuments';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import type React from 'react';

interface DocumentViewerProps {
  document: any | null;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  isOpen,
  onClose,
  title,
  className = 'max-w-7xl w-[95vw] h-[90vh] max-h-[90vh]',
}) => {
  const { data: viewUrl, isLoading: viewLoading } = useDocumentDownloadUrl(
    document?.id,
    { enabled: !!document },
  );

  const getDocumentTitle = () => {
    return title || document?.original_name || 'View Document';
  };

  const renderContent = () => {
    if (viewLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }

    if (!viewUrl) {
      return (
        <div className="text-center text-muted-foreground">
          No preview available.
        </div>
      );
    }

    // Prepare documents array for DocViewer
    const docs = [
      {
        uri: viewUrl,
        fileName: document?.original_name || 'Document',
        fileType: document?.file_type || 'unknown',
      },
    ];

    return (
      <div className="w-full h-full overflow-hidden">
        <DocViewer
          documents={docs}
          pluginRenderers={DocViewerRenderers}
          style={{ height: '100%', width: '100%' }}
          config={{
            header: {
              disableHeader: true,
            },
          }}
        />
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={className}>
        <DialogHeader>
          <DialogTitle>{getDocumentTitle()}</DialogTitle>
        </DialogHeader>
        <div className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
